import { useState } from 'react';
import { useIsDesktop } from '@/hooks/useMediaQuery';
import {
  LAAG_NAAM,
  LAAG_UITLEG,
  STATUS_TEKST,
  statussenVoorLaag,
  type KaartStatus,
  type Kaartlaag,
} from '@/types/domain';
import { StatusStip } from '@/components/ui/StatusStip';
import { ChevronIcoon } from '@/components/layout/Iconen';
import './map.css';

/** Huur of koop; allebei apart aan en uit te zetten. */
export interface SoortFilter {
  huur: boolean;
  koop: boolean;
}

interface LegendaProps {
  aantallen: Record<KaartStatus, number>;
  zichtbaar: Set<KaartStatus>;
  bijSchakel: (status: KaartStatus) => void;
  laag: Kaartlaag;
  bijLaag: (laag: Kaartlaag) => void;
  soort: SoortFilter;
  bijSoort: (soort: keyof SoortFilter) => void;
  aantalHuur: number;
  aantalKoop: number;
}

/**
 * Legenda die tegelijk het filter is.
 *
 * Op een breed scherm staat hij open: daar is ruimte zat en zes regels naast
 * de kaart kosten niets. Op een telefoon en een iPad staat hij dicht, want
 * daar legt hij een hoek van de kaart af waar ook woningen liggen.
 *
 * Bovenaan staat welke vraag de kaart beantwoordt. Twee lagen en geen twee
 * "brillen": een bril is een verborgen stand, en wie een stip aanwijst ziet
 * niet door welke hij kijkt. Een laag heeft een naam die op het scherm blijft
 * staan, met eronder in gewone woorden wat hij betekent.
 */
export function Legenda({
  aantallen,
  zichtbaar,
  bijSchakel,
  laag,
  bijLaag,
  soort,
  bijSoort,
  aantalHuur,
  aantalKoop,
}: LegendaProps): JSX.Element {
  const isBreed = useIsDesktop();
  const [open, setOpen] = useState(isBreed);

  return (
    <div className="legenda">
      <button
        type="button"
        className="legenda__kop"
        onClick={() => setOpen((huidig) => !huidig)}
        aria-expanded={open}
      >
        Statussen
        <ChevronIcoon className={`legenda__chevron${open ? ' legenda__chevron--open' : ''}`} />
      </button>

      {open && (
        <div className="legenda__lijst">
          {/*
            Twee vragen die allebei terecht zijn: gaat de regenpijp van het
            riool af, en gebeurt er daarnaast iets op het perceel. De naam van
            de laag staat op de knop, de betekenis er direct onder.
          */}
          <div className="legenda__schakelaar" role="group" aria-label="Welke vraag de kaart toont">
            <span className="legenda__schakelkop">Kaart toont</span>
            <div className="legenda__knoppen">
              {(['afkoppelen', 'maatregelen'] as const).map((optie) => (
                <button
                  key={optie}
                  type="button"
                  className={`legenda__knop${laag === optie ? ' legenda__knop--aan' : ''}`}
                  aria-pressed={laag === optie}
                  onClick={() => bijLaag(optie)}
                >
                  {LAAG_NAAM[optie]}
                </button>
              ))}
            </div>
            <p className="legenda__laaguitleg">{LAAG_UITLEG[laag]}</p>
          </div>

          {/* Statussen die in deze laag niet kunnen voorkomen laten we weg;
              een regel die altijd op nul staat is ruis. */}
          {statussenVoorLaag(laag).map((status) => {
            const aan = zichtbaar.has(status);
            return (
              <button
                key={status}
                type="button"
                className={`legenda__regel${aan ? '' : ' legenda__regel--uit'}`}
                onClick={() => bijSchakel(status)}
                aria-pressed={aan}
              >
                <StatusStip status={status} formaat={14} />
                <span className="legenda__label">{STATUS_TEKST[laag][status]}</span>
                <span className="legenda__aantal">{aantallen[status] ?? 0}</span>
              </button>
            );
          })}

          {/*
            Soort woning als eigen filter. Zo kun je "alleen de huurwoningen"
            of juist "alleen wat ik zelf moet regelen" op de kaart zetten.
          */}
          <div className="legenda__scheiding">Soort woning</div>

          <button
            type="button"
            className={`legenda__regel${soort.huur ? '' : ' legenda__regel--uit'}`}
            onClick={() => bijSoort('huur')}
            aria-pressed={soort.huur}
          >
            <span className="legenda__driehoek" aria-hidden="true" />
            <span className="legenda__label">Huur — corporatie koppelt af</span>
            <span className="legenda__aantal">{aantalHuur}</span>
          </button>

          <button
            type="button"
            className={`legenda__regel${soort.koop ? '' : ' legenda__regel--uit'}`}
            onClick={() => bijSoort('koop')}
            aria-pressed={soort.koop}
          >
            <span className="legenda__rondje" aria-hidden="true" />
            <span className="legenda__label">Koop — bewoner beslist</span>
            <span className="legenda__aantal">{aantalKoop}</span>
          </button>
        </div>
      )}
    </div>
  );
}
